import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import { getDoctorFilterParams } from '../../utils/doctorFilter';
import {
  Card,
  Button,
  Table,
  FormControl,
  InputGroup,
  Spinner,
  Pagination,
  Badge,
  Form,
  Row,
  Col,
  Modal,
  Alert
} from 'react-bootstrap';
import { FaCalendarPlus, FaSearch, FaEdit, FaTrash, FaPrint, FaUserPlus, FaExclamationTriangle, FaFileInvoiceDollar, FaRedo } from 'react-icons/fa';
import Toast from '../common/Toast';

const Appointments = () => {
  const { user } = useContext(AuthContext);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [appointmentsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [filterStartDate, setFilterStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterEndDate, setFilterEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterDoctor, setFilterDoctor] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [error, setError] = useState(null);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [appointmentToQueue, setAppointmentToQueue] = useState(null);
  const [queueError, setQueueError] = useState(null);
  const [queueFormData, setQueueFormData] = useState({
    doctorId: '',
    notes: '',
    isEmergency: false
  });
  const [queueDoctors, setQueueDoctors] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [appointmentToReschedule, setAppointmentToReschedule] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({
    newDate: '',
    newTime: '',
    reason: '',
    doctorId: ''
  });
  const navigate = useNavigate();

  // Get permissions
  const permissions = user?.role === 'admin' ? {} : (user?.permissions || {});
  const isAdmin = user?.role === 'admin';
  const hasPermission = (key) => isAdmin || permissions[key] === true;

  // Fetch appointments and doctors
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get doctors for filter dropdown
        const doctorsRes = await axios.get('/api/doctors.php?active=1');
        setDoctors(doctorsRes.data || []);

        // Get appointments with initial filter (today's date)
        try {
          const doctorFilter = getDoctorFilterParams(user);
          const params = { startDate: filterStartDate, endDate: filterEndDate, ...doctorFilter };
          const appointmentsRes = await axios.get('/api/appointments.php', { params });
          setAppointments(appointmentsRes.data || []);
          setFilteredAppointments(appointmentsRes.data || []);
          setError(null);
        } catch (appErr) {
          console.error('Error fetching appointments:', appErr);
          setAppointments([]);
          setFilteredAppointments([]);
          setError('Failed to load appointments. Please try again.');
        }
      } catch (err) {
        console.error('Error fetching doctors:', err);
        setDoctors([]);
        setError('Failed to load doctor data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filterStartDate, filterEndDate]);

  // Filter appointments when search or filter criteria change
  useEffect(() => {
    if (!appointments.length) {
      setFilteredAppointments([]);
      return;
    }

    let results = [...appointments];

    if (filterDoctor) {
      results = results.filter(app => app.doctorId === parseInt(filterDoctor));
    }

    if (filterStatus) {
      results = results.filter(app => app.status === filterStatus);
    }

    if (searchQuery) {
      results = results.filter(app => {
        const patientName = app.Patient?.name?.toLowerCase() || '';
        const patientId = app.Patient?.patientId?.toLowerCase() || '';
        const doctorName = app.Doctor?.name?.toLowerCase() || '';
        const appointmentId = app.id?.toString() || '';
        const appointmentTicket = `apt-${app.id}` || '';
        const searchLower = searchQuery.toLowerCase();
        
        return patientName.includes(searchLower) || 
               patientId.includes(searchLower) || 
               doctorName.includes(searchLower) ||
               appointmentId.includes(searchLower) ||
               appointmentTicket.includes(searchLower);
      });
    }

    setFilteredAppointments(results);
    setCurrentPage(1);
  }, [searchQuery, filterDoctor, filterStatus, appointments]);

  // Apply filters
  const handleApplyFilters = async () => {
    setLoading(true);
    try {
      const doctorFilter = getDoctorFilterParams(user);
      const params = { ...doctorFilter };
      
      if (filterStartDate) {
        params.startDate = filterStartDate;
      }
      
      if (filterEndDate) {
        params.endDate = filterEndDate;
      }
      
      if (filterDoctor) {
        params.doctorId = filterDoctor;
      }
      
      if (filterStatus) {
        params.status = filterStatus;
      }
      
      const res = await axios.get('/api/appointments.php', { params });
      setAppointments(res.data || []);
      setFilteredAppointments(res.data || []);
      setError(null);
    } catch (err) {
      console.error('Error applying filters:', err);
      setAppointments([]);
      setFilteredAppointments([]);
      setError('Failed to filter appointments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete appointment
  const handleDeleteClick = (appointment) => {
    setAppointmentToDelete(appointment);
    setShowDeleteModal(true);
  };

  // Handle add to queue
  const handleAddToQueueClick = async (appointment) => {
    setAppointmentToQueue(appointment);
    setQueueError(null);
    
    // CRITICAL: Get internal patient ID
    const internalPatientId = appointment.Patient?.id || appointment.patientId;
    
    // Check if appointment date is today
    const today = new Date().toISOString().split('T')[0];
    const appointmentDate = appointment.appointmentDate;
    
    if (appointmentDate !== today) {
      setToastMessage(`Cannot add to queue! Appointment is scheduled for ${new Date(appointmentDate).toLocaleDateString('en-GB')}. Patient can only join queue on appointment date.`);
      setToastType('error');
      setShowToast(true);
      return;
    }
    
    setQueueFormData({
      doctorId: appointment.doctorId.toString(),
      notes: '',
      isEmergency: false
    });
    
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // CRITICAL: Use Patient.id (internal database ID) not patientId (display ID)
      const internalPatientId = appointment.Patient?.id || appointment.patientId;
      
      console.log('=== Queue Check Debug ===');
      console.log('Appointment:', appointment);
      console.log('Internal Patient ID:', internalPatientId);
      console.log('Display Patient ID:', appointment.Patient?.patientId);
      
      const billsRes = await axios.get(`/api/billings.php?patientId=${internalPatientId}`);
      const todayPaidBills = billsRes.data.filter(bill => {
        // Extract date from createdAt (format: '2026-04-01 03:47:48') or billDate
        let billDate;
        if (bill.createdAt) {
          billDate = bill.createdAt.split(' ')[0];
        } else if (bill.billDate) {
          billDate = bill.billDate.split(' ')[0];
        } else if (bill.date) {
          billDate = bill.date;
        }
        
        const isToday = billDate === today;
        const isPaid = bill.paymentStatus === 'complete';
        const hasDoctor = bill.doctorId;
        const isNotUsed = !bill.billStatus || bill.billStatus === 'not-used';
        
        console.log('Bill check:', {
          billId: bill.id,
          billNumber: bill.billNumber,
          billDate,
          today,
          isToday,
          isPaid,
          hasDoctor,
          billStatus: bill.billStatus,
          isNotUsed,
          MATCH: isToday && isPaid && hasDoctor && isNotUsed
        });
        
        return isToday && isPaid && hasDoctor && isNotUsed;
      });
      
      console.log('Debug queue check:', {
        appointmentPatientId: appointment.patientId,
        appointmentInternalId: internalPatientId,
        billsFound: billsRes.data.length,
        todayPaidBills: todayPaidBills.length,
        today: today,
        billDetails: todayPaidBills.map(b => ({id: b.id, patientId: b.patientId, doctorId: b.doctorId, status: b.paymentStatus, billStatus: b.billStatus, date: b.createdAt}))
      });
      
      if (todayPaidBills.length === 0) {
        setToastMessage('Payment required! Please complete payment with a doctor before adding to queue.');
        setToastType('error');
        setShowToast(true);
        return;
      }
      
      const queueRes = await axios.get(`/api/queue.php?date=${today}`);
      const todayQueueEntries = queueRes.data.filter(q => q.patientId === internalPatientId);
      
      const paymentsByDoctor = {};
      todayPaidBills.forEach(bill => {
        paymentsByDoctor[bill.doctorId] = (paymentsByDoctor[bill.doctorId] || 0) + 1;
      });
      
      const queueByDoctor = {};
      todayQueueEntries.forEach(q => {
        queueByDoctor[q.doctorId] = (queueByDoctor[q.doctorId] || 0) + 1;
      });
      
      const availableDoctorIds = Object.keys(paymentsByDoctor).filter(doctorId => {
        const payments = paymentsByDoctor[doctorId];
        const queueEntries = queueByDoctor[doctorId] || 0;
        return payments > queueEntries;
      }).map(id => parseInt(id));
      
      if (availableDoctorIds.length === 0) {
        setToastMessage('No queue slots available. Please make another payment to add to queue again.');
        setToastType('error');
        setShowToast(true);
        return;
      }
      
      const doctorsRes = await axios.get('/api/doctors.php?active=1');
      const availableDoctors = doctorsRes.data.filter(doc => availableDoctorIds.includes(doc.id));
      
      if (availableDoctors.length > 0) {
        setQueueDoctors(availableDoctors);
        setQueueFormData({
          doctorId: appointment.doctorId.toString(),
          notes: '',
          isEmergency: false
        });
        setShowQueueModal(true);
      } else {
        setToastMessage('No doctors available. Please complete payment with a doctor first.');
        setToastType('error');
        setShowToast(true);
      }
    } catch (err) {
      console.error('Error fetching doctors:', err);
      setToastMessage('Failed to load doctors. Please try again.');
      setToastType('error');
      setShowToast(true);
    }
  };
  
  const handleQueueFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setQueueFormData({
      ...queueFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleSubmitQueue = async (e) => {
    e.preventDefault();
    
    if (!appointmentToQueue) {
      setQueueError('Appointment not selected.');
      return;
    }
    
    if (!queueFormData.doctorId) {
      setQueueError('Please select a doctor.');
      return;
    }
    
    // Get internal patient ID from appointment
    const internalPatientId = appointmentToQueue.Patient?.id || appointmentToQueue.patientId;
    
    try {
      await axios.post('/api/queue.php', {
        patientId: internalPatientId,
        doctorId: parseInt(queueFormData.doctorId),
        appointmentId: appointmentToQueue.id,
        notes: queueFormData.notes,
        isEmergency: queueFormData.isEmergency
      });
      
      setShowQueueModal(false);
      
      // Get patient name from the appointment data
      const patientName = appointmentToQueue.Patient?.name || appointmentToQueue.patientName || 'Patient';
      
      setToastMessage(`${patientName} added to queue successfully!`);
      setToastType('success');
      setShowToast(true);
      
      const res = await axios.get('/api/appointments.php');
      setAppointments(res.data);
      setFilteredAppointments(res.data);
    } catch (err) {
      console.error('Error adding to queue:', err);
      setQueueError(err.response?.data?.msg || 'Failed to add patient to queue. Please try again.');
    }
  };

  const handleConfirmDelete = async () => {
    if (!appointmentToDelete) return;
    
    try {
      await axios.delete(`/api/appointments/${appointmentToDelete.id}`);
      setAppointments(appointments.filter(a => a.id !== appointmentToDelete.id));
      setFilteredAppointments(filteredAppointments.filter(a => a.id !== appointmentToDelete.id));
      setShowDeleteModal(false);
      setAppointmentToDelete(null);
      setError(null);
    } catch (err) {
      console.error('Error deleting appointment:', err);
      setError('Failed to delete appointment. Please try again.');
    }
  };

  // Handle reschedule
  const handleRescheduleClick = async (appointment) => {
    try {
      const queueRes = await axios.get(`/api/queue.php?appointmentId=${appointment.id}`);
      const queueEntry = queueRes.data.find(q => q.appointmentId === appointment.id);
      
      if (queueEntry) {
        setAppointmentToReschedule({ ...appointment, queueId: queueEntry.id });
      } else {
        setAppointmentToReschedule(appointment);
      }
      
      // Get doctors who have received payment from this patient
      const billsRes = await axios.get(`/api/billings.php?patientId=${appointment.patientId}`);
      const paidDoctorIds = [...new Set(
        billsRes.data
          .filter(bill => bill.paymentStatus === 'complete' && bill.doctorId)
          .map(bill => bill.doctorId)
      )];
      
      // Filter doctors to show only those with payment
      const filteredDoctors = doctors.filter(doc => paidDoctorIds.includes(doc.id));
      
      // If no doctors with payment, show all doctors
      setDoctors(filteredDoctors.length > 0 ? filteredDoctors : doctors);
      
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setRescheduleData({
        newDate: tomorrow.toISOString().split('T')[0],
        newTime: appointment.appointmentTime || '10:00',
        reason: '',
        doctorId: appointment.doctorId.toString()
      });
      setShowRescheduleModal(true);
    } catch (err) {
      console.error('Error checking queue:', err);
      setAppointmentToReschedule(appointment);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setRescheduleData({
        newDate: tomorrow.toISOString().split('T')[0],
        newTime: appointment.appointmentTime || '10:00',
        reason: '',
        doctorId: appointment.doctorId.toString()
      });
      setShowRescheduleModal(true);
    }
  };

  const handleConfirmReschedule = async () => {
    if (!appointmentToReschedule) return;
    
    try {
      // Get existing appointment data first
      const existingRes = await axios.get(`/api/appointments.php?id=${appointmentToReschedule.id}`);
      const existingAppointment = existingRes.data;
      
      console.log('=== Reschedule Debug ===');
      console.log('Existing appointment:', existingAppointment);
      console.log('Patient ID from appointment:', existingAppointment.patientId);
      console.log('Patient internal ID:', existingAppointment.patient_internal_id);
      console.log('Patient object:', existingAppointment.Patient);
      
      // CRITICAL: Don't send patientId if it's not changing (to avoid validation issues)
      // Only send fields that are actually changing
      const updateData = {
        doctorId: parseInt(rescheduleData.doctorId),
        appointmentDate: rescheduleData.newDate,
        appointmentTime: rescheduleData.newTime,
        status: 'rescheduled',
        appointmentType: existingAppointment.appointmentType || 'regular',
        roomNumber: existingAppointment.roomNumber || '',
        notes: `${appointmentToReschedule.notes || ''}\nOriginal: ${appointmentToReschedule.appointmentDate} at ${formatTime(appointmentToReschedule.appointmentTime)}\nRescheduled: ${rescheduleData.reason}`
      };
      
      // Only include patientId if it's actually changing (which it shouldn't for reschedule)
      // This avoids unnecessary validation
      
      console.log('Update data being sent (without patientId):', updateData);
      
      const response = await axios.put(`/api/appointments.php?id=${appointmentToReschedule.id}`, updateData);
      
      // If there's a queue entry, cancel it
      if (appointmentToReschedule.queueId) {
        try {
          await axios.put('/api/queue.php', {
            id: appointmentToReschedule.queueId,
            status: 'cancelled',
            notes: `Cancelled due to reschedule: ${rescheduleData.reason}`
          });
        } catch (queueErr) {
          console.error('Error cancelling queue:', queueErr);
        }
      }
      
      setToastMessage('Appointment rescheduled successfully!');
      setToastType('success');
      setShowToast(true);
      setShowRescheduleModal(false);
      handleApplyFilters();
    } catch (err) {
      console.error('Error rescheduling:', err);
      console.error('Error details:', err.response?.data);
      const errorMsg = err.response?.data?.error || err.response?.data?.msg || 'Failed to reschedule appointment.';
      setToastMessage(errorMsg);
      setToastType('error');
      setShowToast(true);
    }
  };

  // Pagination
  const indexOfLastAppointment = currentPage * appointmentsPerPage;
  const indexOfFirstAppointment = indexOfLastAppointment - appointmentsPerPage;
  const currentAppointments = filteredAppointments.slice(indexOfFirstAppointment, indexOfLastAppointment);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Format time to display in 12-hour format
  const formatTime = (time) => {
    if (!time) return '';
    
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12;
      return `${formattedHour}:${minutes} ${ampm}`;
    } catch (err) {
      console.error('Error formatting time:', err);
      return time; // Return original value if formatting fails
    }
  };

  // Get status badge style
  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <Badge bg="primary">Scheduled</Badge>;
      case 'confirmed':
        return <Badge bg="success">Confirmed</Badge>;
      case 'completed':
        return <Badge bg="info">Completed</Badge>;
      case 'cancelled':
        return <Badge bg="danger">Cancelled</Badge>;
      case 'no-show':
        return <Badge bg="warning">No Show</Badge>;
      case 'rescheduled':
        return <Badge bg="warning">Rescheduled</Badge>;
      default:
        return <Badge bg="secondary">{status || 'Unknown'}</Badge>;
    }
  };

  // Get appointment type badge
  const getAppointmentTypeBadge = (type) => {
    switch (type) {
      case 'emergency':
        return <Badge bg="danger">Emergency</Badge>;
      case 'walkin':
        return <Badge bg="warning">Walk-in</Badge>;
      case 'followup':
        return <Badge bg="info">Follow-up</Badge>;
      default:
        return <Badge bg="secondary">{type || 'Regular'}</Badge>;
    }
  };

  return (
    <div>
      <Toast 
        show={showToast} 
        onClose={() => setShowToast(false)} 
        message={toastMessage} 
        type={toastType}
      />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Appointments</h2>
        {hasPermission('bookAppointment') && (
          <Link to="/appointments/add">
            <Button variant="primary">
              <FaCalendarPlus className="me-2" /> New Appointment
            </Button>
          </Link>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Header>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Doctor</Form.Label>
                <Form.Select
                  value={filterDoctor}
                  onChange={(e) => setFilterDoctor(e.target.value)}
                >
                  <option value="">All Doctors</option>
                  {doctors.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="no-show">No Show</option>
                  <option value="rescheduled">Rescheduled</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="primary"
                onClick={handleApplyFilters}
                className="mb-3 w-100"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Loading...
                  </>
                ) : (
                  'Apply Filters'
                )}
              </Button>
            </Col>
          </Row>
          <InputGroup className="mt-3">
            <FormControl
              placeholder="Search by patient name, ID, doctor name or ticket number"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button variant="outline-secondary">
              <FaSearch />
            </Button>
          </InputGroup>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : filteredAppointments.length === 0 ? (
            <div className="text-center py-4">
              <p className="mb-0">No appointments found for selected criteria.</p>
            </div>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Appointment Day</th>
                    <th>Booked On</th>
                    <th>Patient</th>
                    <th>Doctor</th>
                    <th>Room</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Bill #</th>
                    <th>Queue Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentAppointments.map((appointment) => {
                    const appointmentDate = new Date(appointment.appointmentDate);
                    const dayName = appointmentDate.toLocaleDateString('en-US', { weekday: 'short' });
                    const dateStr = appointmentDate.toLocaleDateString('en-GB');
                    const appointmentTime = formatTime(appointment.appointmentTime);
                    
                    const bookedDate = new Date(appointment.createdAt.replace(' ', 'T'));
                    // Add 5 hours for Pakistan timezone (UTC+5)
                    bookedDate.setHours(bookedDate.getHours() + 5);
                    const bookedDay = bookedDate.toLocaleDateString('en-US', { weekday: 'short' });
                    const bookedDateStr = bookedDate.toLocaleDateString('en-GB');
                    const hours = bookedDate.getHours();
                    const minutes = bookedDate.getMinutes().toString().padStart(2, '0');
                    const ampm = hours >= 12 ? 'PM' : 'AM';
                    const formattedHour = hours % 12 || 12;
                    const bookedTime = `${formattedHour}:${minutes} ${ampm}`;
                    
                    return (
                    <tr key={appointment.id}>
                      <td>
                        <div>{dayName}</div>
                        <div className="small text-muted">{dateStr}</div>
                        <div className="small text-muted">{appointmentTime}</div>
                      </td>
                      <td>
                        <div>{bookedDay}</div>
                        <div className="small text-muted">{bookedDateStr}</div>
                        <div className="small text-muted">{bookedTime}</div>
                      </td>
                      <td>
                        {appointment.patientName ? (
                          <>
                            <span className="text-decoration-none">
                              {appointment.patientName}
                            </span>
                            <div className="small text-muted">
                              ID: {appointment.patientId || 'N/A'}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted">Unknown Patient</span>
                        )}
                      </td>
                      <td>{appointment.doctorName || 'N/A'}</td>
                      <td>{appointment.roomNumber || '-'}</td>
                      <td>{getAppointmentTypeBadge(appointment.appointmentType)}</td>
                      <td>{getStatusBadge(appointment.status)}</td>
                      <td>
                        {appointment.billNumber ? (
                          <Badge bg="info" title={`Bill ID: ${appointment.billId}`}>
                            {appointment.billNumber}
                          </Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {appointment.queueStatus === 'available' && <Badge bg="success">Queue Available</Badge>}
                        {appointment.queueStatus === 'waiting' && <Badge bg="warning">In Queue</Badge>}
                        {appointment.queueStatus === 'in-progress' && <Badge bg="primary">With Doctor</Badge>}
                        {!appointment.queueStatus && <span className="text-muted">-</span>}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          {(hasPermission('editBill') || hasPermission('deletePatient')) && (
                            <div className="btn-group me-1">
                              {hasPermission('editBill') && (
                                <Link to={`/appointments/edit/${appointment.id}`} className="btn btn-sm btn-outline-primary">
                                  <FaEdit />
                                </Link>
                              )}
                              {hasPermission('deletePatient') && (
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  onClick={() => handleDeleteClick(appointment)}
                                >
                                  <FaTrash />
                                </Button>
                              )}
                            </div>
                          )}
                          {hasPermission('payment') && (
                            <Link 
                              to="/create-bill"
                              state={{ 
                                patientId: appointment.Patient?.id || appointment.patientId,
                                doctorId: appointment.doctorId,
                                appointmentId: appointment.id
                              }}
                              className="btn btn-sm btn-info me-1"
                              onClick={() => {
                                console.log('=== Pay Button Clicked ===');
                                console.log('Appointment data:', appointment);
                                console.log('Patient ID being passed:', appointment.Patient?.id || appointment.patientId);
                                console.log('Doctor ID being passed:', appointment.doctorId);
                                console.log('Appointment ID being passed:', appointment.id);
                              }}
                            >
                              <FaFileInvoiceDollar /> Pay
                            </Link>
                          )}
                          {hasPermission('addPatient') && (
                            <Button 
                              variant="outline-success" 
                              size="sm"
                              className="me-1"
                              onClick={() => handleAddToQueueClick(appointment)}
                            >
                              <FaUserPlus /> Queue
                            </Button>
                          )}
                          {hasPermission('printBill') && (
                            <Button 
                              variant="outline-info" 
                              size="sm"
                              className="me-1"
                              onClick={() => navigate(`/ticket/appointment/${appointment.id}`)}
                            >
                              <FaPrint />
                            </Button>
                          )}
                          {['scheduled', 'confirmed', 'no-show', 'rescheduled'].includes(appointment.status) && hasPermission('rescheduleAppointment') && (
                            <Button 
                              variant="outline-warning" 
                              size="sm"
                              onClick={() => handleRescheduleClick(appointment)}
                              title="Reschedule appointment"
                            >
                              <FaRedo className="me-1" /> Reschedule
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </Table>

              <div className="d-flex justify-content-center mt-4">
                <Pagination>
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                  
                  {Array.from(
                    { length: Math.ceil(filteredAppointments.length / appointmentsPerPage) },
                    (_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => paginate(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    )
                  )}
                  
                  <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredAppointments.length / appointmentsPerPage)))} disabled={currentPage === Math.ceil(filteredAppointments.length / appointmentsPerPage)} />
                  <Pagination.Last onClick={() => setCurrentPage(Math.ceil(filteredAppointments.length / appointmentsPerPage))} disabled={currentPage === Math.ceil(filteredAppointments.length / appointmentsPerPage)} />
                </Pagination>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {appointmentToDelete && (
            <>
              Are you sure you want to delete this appointment for{' '}
              <strong>{appointmentToDelete.Patient?.name || 'Unknown Patient'}</strong> on{' '}
              <strong>
                {appointmentToDelete.appointmentDate} at{' '}
                {formatTime(appointmentToDelete.appointmentTime)}
              </strong>
              ?
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add to Queue Modal */}
      <Modal show={showQueueModal} onHide={() => setShowQueueModal(false)}>
        <Modal.Header>
          <Modal.Title>Add Patient to Queue</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitQueue}>
          <Modal.Body>
            {queueError && (
              <Alert variant="danger" className="mb-3">
                {queueError}
              </Alert>
            )}

            <div className="mb-3">
              <strong>Patient:</strong> {appointmentToQueue?.Patient?.name}{' '}
              <span className="text-muted">({appointmentToQueue?.Patient?.age} yrs, {appointmentToQueue?.Patient?.gender})</span>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Select Doctor *</Form.Label>
              <Form.Select
                name="doctorId"
                value={queueFormData.doctorId}
                onChange={handleQueueFormChange}
                required
              >
                <option value="">-- Select Doctor --</option>
                {queueDoctors.map(doctor => (
                  <option key={doctor.id} value={doctor.id}>
                    Dr. {doctor.name} - {doctor.specialization}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="notes"
                value={queueFormData.notes}
                onChange={handleQueueFormChange}
                placeholder="Add any notes about this patient's visit"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="emergency"
                name="isEmergency"
                label={
                  <>
                    <FaExclamationTriangle className="text-danger me-1" /> 
                    Mark as Emergency (will be prioritized in queue)
                  </>
                }
                checked={queueFormData.isEmergency}
                onChange={handleQueueFormChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowQueueModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add to Queue
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Reschedule Modal */}
      <Modal show={showRescheduleModal} onHide={() => setShowRescheduleModal(false)}>
        <Modal.Header>
          <Modal.Title>Reschedule Appointment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>Patient:</strong> {appointmentToReschedule?.Patient?.name}
            <br />
            <strong>Current Date:</strong> {appointmentToReschedule?.appointmentDate}
            <br />
            <strong>Current Time:</strong> {formatTime(appointmentToReschedule?.appointmentTime)}
            <br />
            <strong>Status:</strong> {appointmentToReschedule?.status}
          </p>
          
          {appointmentToReschedule?.status === 'no-show' && (
            <Alert variant="info">
              💰 Payment will be transferred to new appointment (valid for 30 days)
            </Alert>
          )}
          
          <Form.Group className="mb-3">
            <Form.Label>New Date *</Form.Label>
            <Form.Control
              type="date"
              value={rescheduleData.newDate}
              onChange={(e) => setRescheduleData({...rescheduleData, newDate: e.target.value})}
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>New Time *</Form.Label>
            <Form.Control
              type="time"
              value={rescheduleData.newTime}
              onChange={(e) => setRescheduleData({...rescheduleData, newTime: e.target.value})}
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Doctor</Form.Label>
            <Form.Select
              value={rescheduleData.doctorId}
              onChange={(e) => setRescheduleData({...rescheduleData, doctorId: parseInt(e.target.value)})}
            >
              {doctors.map(doctor => (
                <option key={doctor.id} value={doctor.id}>
                  Dr. {doctor.name} - {doctor.specialization}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Reason *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rescheduleData.reason}
              onChange={(e) => setRescheduleData({...rescheduleData, reason: e.target.value})}
              placeholder="Enter reason for rescheduling"
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRescheduleModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmReschedule}>
            Confirm Reschedule
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Appointments;





